import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, BedDouble, Ruler } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { CORPORATE_HOMES } from "@/lib/constants";

export default function Firmaboliger() {
  return (
    <div className="bg-white text-[#222222]" data-testid="firmaboliger-page">
      <Header />

      <main>
        {/* Hero */}
        <section className="bg-[#111111] text-white pt-32 sm:pt-40 pb-16 sm:pb-24 relative overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] glow-gold pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="overline text-[10px] sm:text-xs mb-5 sm:mb-7"
              style={{ color: "#D4AF37" }}
            >
              <span className="inline-block w-8 sm:w-12 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Til virksomheder
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-[2.5rem] sm:text-6xl lg:text-[5.5rem] font-light leading-[1.02] tracking-[-0.02em] text-white max-w-5xl"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Firmaboliger i{" "}
              <em className="italic text-[#D4AF37]">hele Danmark</em>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-6 sm:mt-10 max-w-2xl text-white/80 text-base sm:text-xl leading-relaxed font-light"
            >
              Møblerede boliger til medarbejdere, montører og projektansatte —
              fra kortere ophold til flere måneder.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-8 max-w-2xl text-white/60 text-sm sm:text-base leading-relaxed font-light"
            >
              Vi hjælper virksomheder med midlertidig indkvartering af medarbejdere
              og eksterne konsulenter i hele landet. Ét kontaktpunkt, én fast
              månedspris og alt det praktiske omkring opholdet håndteret af os -
              så jeres team kan fokusere på arbejdet i stedet for boligsøgning.
            </motion.p>
          </div>
        </section>

        {/* Property cards */}
        <section className="bg-white py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-end justify-between mb-10 sm:mb-14">
              <div>
                <p className="overline mb-4 text-[10px] sm:text-xs">
                  <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
                  Tilgængelige boliger
                </p>
                <h2
                  className="font-serif text-[1.75rem] sm:text-4xl lg:text-5xl text-[#111111] font-light tracking-tight leading-[1.05]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Vores katalog af firmaboliger.
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {CORPORATE_HOMES.map((home, i) => (
                <motion.article
                  key={home.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.9,
                    delay: i * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="group"
                  data-testid={`home-card-${home.slug}`}
                >
                  <Link
                    to={`/firmaboliger/${home.slug}`}
                    className="block"
                    data-testid={`home-card-link-${home.slug}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#F1EFEA]">
                      <img
                        src={home.coverImage}
                        alt={home.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                      />
                      <div className="absolute top-4 left-4 bg-[#111111]/85 text-white text-[10px] tracking-[0.28em] uppercase px-3 py-1.5">
                        Firmabolig
                      </div>
                    </div>
                    <div className="pt-6">
                      <div className="flex items-baseline justify-between gap-4 mb-2">
                        <h3
                          className="font-serif text-2xl sm:text-[1.75rem] text-[#111111] font-light leading-tight tracking-tight"
                          style={{ fontFamily: "Fraunces, serif" }}
                        >
                          {home.shortTitle}
                        </h3>
                        <span className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] shrink-0">
                          {home.area}
                        </span>
                      </div>
                      <p className="text-[#555555] font-light leading-relaxed max-w-md">
                        {home.tagline}
                      </p>

                      <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[#333333]">
                        {home.guests && (
                          <li className="inline-flex items-center gap-2">
                            <Users size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
                            {home.guests} personer
                          </li>
                        )}
                        {home.bedrooms && (
                          <li className="inline-flex items-center gap-2">
                            <BedDouble size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
                            {home.bedrooms}
                          </li>
                        )}
                        {home.sqm && (
                          <li className="inline-flex items-center gap-2">
                            <Ruler size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
                            ca. {home.sqm} m²
                          </li>
                        )}
                        {!home.guests && !home.bedrooms && !home.sqm && (
                          <li className="text-[#555555] font-light">Kontakt os for detaljer</li>
                        )}
                      </ul>

                      {home.amenities?.length > 0 && (
                        <ul className="mt-5 flex flex-wrap gap-2">
                          {home.amenities.slice(0, 5).map((a) => (
                            <li
                              key={a}
                              className="text-[11px] tracking-wide text-[#555555] border border-black/10 px-2.5 py-1"
                            >
                              {a}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="mt-6 inline-flex items-center gap-3 text-[#111111] group-hover:text-[#C9A227] transition-colors">
                        <span className="text-sm tracking-[0.16em] uppercase">
                          Se boligen
                        </span>
                        <ArrowRight
                          size={16}
                          strokeWidth={1.5}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Placeholder / how it works */}
            <div className="mt-20 sm:mt-28 border-t border-black/10 pt-12 sm:pt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <p className="overline mb-4 text-[10px] sm:text-xs">
                  <span className="inline-block w-8 h-px bg-[#D4AF37] align-middle mr-3" />
                  Sådan fungerer det
                </p>
                <h3
                  className="font-serif text-[1.5rem] sm:text-3xl text-[#111111] font-light leading-tight"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Fra forespørgsel til flytteklar bolig - typisk 24-48 timer.
                </h3>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
                {[
                  {
                    n: "01",
                    t: "Forespørg",
                    d: "Send os en kort forespørgsel med datoer, antal personer og eventuelle ønsker.",
                  },
                  {
                    n: "02",
                    t: "Bekræftelse",
                    d: "Vi bekræfter tilgængelighed, sender pris og en klar aftale samme dag.",
                  },
                  {
                    n: "03",
                    t: "Indflytning",
                    d: "Nøgler, Wi-Fi, sengetøj og alt praktisk klar til jeres medarbejdere.",
                  },
                ].map((s) => (
                  <div key={s.n} className="border-t border-black/10 pt-5">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
                      {s.n}
                    </div>
                    <div
                      className="font-serif text-xl text-[#111111] font-light mt-2"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {s.t}
                    </div>
                    <p className="text-sm text-[#555555] font-light leading-relaxed mt-2">
                      {s.d}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
